import i18n from "../../i18n";
import {
  TASK_STATUS_LABELS as TASK_STATUS_LABELS_FALLBACK,
  TASK_TYPE_LABELS as TASK_TYPE_LABELS_FALLBACK,
  taskStatusColor,
} from "./skillFichaHelpers";
import { rapportToolKind } from "./skillAgentRapport";
import {
  ORG_BRANDS,
  normalizeOrgBrand,
  partitionRowsByOrgBrand,
} from "./skillAgentOrgBrand";
import {
  isClientInfoRow,
  isPresentacionesRow,
  isProduccionRow,
  isStatusRow,
} from "./skillAgentClientInfoKind";

function tt(key, fallback, vars) {
  const translated = i18n.t(key, vars);
  if (translated === key) return fallback;
  return translated;
}

export function taskClientLabel(row) {
  return (
    row?.CustomerName ||
    (row?.CustomerId
      ? tt("skillAgent.tree.clientNumber", `Cliente #${row.CustomerId}`, {
          id: row.CustomerId,
        })
      : null) ||
    row?.SkillSlug ||
    tt("skillAgent.tree.noClient", "Sin cliente")
  );
}

export function taskSeasonLabel(row) {
  const season = String(row?.Temporada || "").trim();
  return season || tt("skillAgent.tree.noSeason", "Sin temporada");
}

/** Siluetas / Nuevos diseños en el diálogo Compás → abren AI Designs. */
export function isSketchTipoTarea(tipo) {
  const t = String(tipo || "")
    .trim()
    .toLowerCase();
  return t === "siluetas" || t === "nuevos_disenos";
}

/** Pieza AI Designs colgada en SKETCH / NEW DESIGNS. */
export function isAiDesignBoard(row) {
  const k = String(row?.Kind || row?.kind || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return (
    k === "ai_design" ||
    k === "aidesign" ||
    k === "sketch" ||
    k === "sketches" ||
    k === "silueta" ||
    k === "siluetas" ||
    k === "nuevo_diseno" ||
    k === "nuevos_disenos"
  );
}

/** Collage PROPUESTAS (pool Sketch / meeting / SEARCH; no Recaps). */
export function isPropuestaBoard(row) {
  const k = String(row?.Kind || row?.kind || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return k === "propuesta" || k === "propuestas";
}

export function searchStatusLabel(status) {
  const key = `skillAgent.searchStatus.${status}`;
  const mapFallback = {
    draft: "Borrador",
    running: "Buscando…",
    done: "Listo",
    error: "Error",
    cancelled: "Cancelado",
  };
  if (status && i18n.exists(key)) return i18n.t(key);
  return mapFallback[status] || status || tt("skillAgent.common.emDash", "—");
}

export function moodboardStatusLabel(status) {
  const key = `skillAgent.moodboardStatus.${status}`;
  const mapFallback = {
    draft: "Borrador",
    ready: "PDF listo",
  };
  if (status && i18n.exists(key)) return i18n.t(key);
  return mapFallback[status] || status || tt("skillAgent.common.emDash", "—");
}

export function rapportStatusLabel(status) {
  const key = `skillAgent.rapportStatus.${status}`;
  const mapFallback = {
    draft: "Borrador",
    ready: "Tile listo",
  };
  if (status && i18n.exists(key)) return i18n.t(key);
  return mapFallback[status] || status || tt("skillAgent.common.emDash", "—");
}

/** Tablero de la rama Rapports (mismo editor que moodboard; Kind=resumen). */
export function isResumenBoard(row) {
  const k = String(row?.Kind || row?.kind || "")
    .trim()
    .toLowerCase();
  return k === "resumen" || k === "resumenes" || k === "summary";
}

export function isFichaTecnicaBoard(row) {
  const k = String(row?.Kind || row?.kind || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return (
    k === "ficha_tecnica" ||
    k === "fichatecnica" ||
    k === "tech_pack" ||
    k === "techpack"
  );
}

export function taskStatusLabel(status) {
  const key = `skillAgent.taskStatus.${status}`;
  if (status && i18n.exists(key)) return i18n.t(key);
  return TASK_STATUS_LABELS_FALLBACK[status] || status || "—";
}

export function taskTypeLabel(type) {
  const key = `skillAgent.taskType.${type}`;
  if (type && i18n.exists(key)) return i18n.t(key);
  return TASK_TYPE_LABELS_FALLBACK[type] || type || "—";
}

/** @deprecated prefer taskStatusLabel(); kept for callers that import the map */
export const TASK_STATUS_LABELS = TASK_STATUS_LABELS_FALLBACK;
/** @deprecated prefer taskTypeLabel() */
export const TASK_TYPE_LABELS = TASK_TYPE_LABELS_FALLBACK;

export { taskStatusColor };

/**
 * groupBy: "season" → Temporada → Cliente → PROPUESTAS + SEARCHS + MOODBOARDS + Recaps + PRINTS
 * groupBy: "client" → Cliente → Temporada → …
 */
export function buildAgentTasksTree(
  rows = [],
  groupBy = "season",
  searches = [],
  moodboards = [],
  rapports = [],
) {
  const filtered = Array.isArray(rows) ? rows : [];
  const searchRows = Array.isArray(searches) ? searches : [];
  const boardRows = Array.isArray(moodboards) ? moodboards : [];
  const rapportRows = Array.isArray(rapports) ? rapports : [];
  const rootMap = new Map();
  const noSeason = tt("skillAgent.tree.noSeason", "Sin temporada");
  const noClient = tt("skillAgent.tree.noClient", "Sin cliente");
  const folderDesigns = tt("skillAgent.tree.folderDesigns", "SKILLS/AGENTES");
  const folderSketches = tt(
    "skillAgent.tree.folderSketches",
    "SKETCH / NEW DESIGNS",
  );
  const folderSearchs = tt("skillAgent.tree.folderSearchs", "SEARCHS");
  const folderMoodboards = tt("skillAgent.tree.folderMoodboards", "MOODBOARDS");
  const folderPropuestas = tt("skillAgent.tree.folderPropuestas", "PROPUESTAS");
  const folderFichas = tt(
    "skillAgent.tree.folderFichasTecnicas",
    "FICHAS TÉCNICAS",
  );
  const folderResumenes = tt("skillAgent.tree.folderResumenes", "RECAPS");
  const folderRapports = tt("skillAgent.tree.folderRapports", "PRINTS");
  const folderRapportTiles = tt("skillAgent.tree.folderRapportTiles", "TARTÁN");
  const folderRapportInterpret = tt(
    "skillAgent.tree.folderRapportInterpret",
    "SEAMLESS",
  );

  function ensureL2(L1, L2, kinds) {
    if (!rootMap.has(L1)) {
      rootMap.set(L1, {
        id: `l1:${L1}`,
        label: L1,
        kind: kinds.l1,
        children: new Map(),
        tasks: [],
      });
    }
    const n1 = rootMap.get(L1);
    if (!n1.children.has(L2)) {
      n1.children.set(L2, {
        id: `l2:${L1}::${L2}`,
        label: L2,
        kind: kinds.l2,
        tasks: [],
        searches: [],
        moodboards: [],
        propuestas: [],
        fichasTecnicas: [],
        resumenes: [],
        rapports: [],
      });
    }
    return n1.children.get(L2);
  }

  const kinds =
    groupBy === "client"
      ? { l1: "client", l2: "season" }
      : { l1: "season", l2: "client" };

  for (const row of filtered) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).tasks.push(row);
  }

  for (const row of searchRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).searches.push(row);
  }

  for (const row of boardRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    const bucket = ensureL2(L1, L2, kinds);
    if (isResumenBoard(row)) bucket.resumenes.push(row);
    else if (isFichaTecnicaBoard(row)) bucket.fichasTecnicas.push(row);
    else if (isPropuestaBoard(row)) {
      if (!bucket.propuestas) bucket.propuestas = [];
      bucket.propuestas.push(row);
    } else bucket.moodboards.push(row);
  }

  for (const row of rapportRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).rapports.push(row);
  }

  const sortLabel = (a, b) =>
    String(a).localeCompare(String(b), i18n.language || "es", {
      numeric: true,
      sensitivity: "base",
    });

  const roots = [...rootMap.values()]
    .map((n1) => {
      const children = [...n1.children.values()]
        .map((n2) => {
          const tasks = [...n2.tasks].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const searchesSorted = [...(n2.searches || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const moodboardsSorted = [...(n2.moodboards || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const propuestasSorted = [...(n2.propuestas || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const fichasSorted = [...(n2.fichasTecnicas || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const resumenesSorted = [...(n2.resumenes || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const rapportsSorted = [...(n2.rapports || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const ctxCustomerId =
            tasks.find((t) => t.CustomerId)?.CustomerId ||
            searchesSorted.find((s) => s.CustomerId)?.CustomerId ||
            moodboardsSorted.find((m) => m.CustomerId)?.CustomerId ||
            fichasSorted.find((m) => m.CustomerId)?.CustomerId ||
            resumenesSorted.find((m) => m.CustomerId)?.CustomerId ||
            rapportsSorted.find((r) => r.CustomerId)?.CustomerId ||
            null;
          const Temporada = groupBy === "client" ? n2.label : n1.label;
          const CustomerName = groupBy === "client" ? n1.label : n2.label;
          const branchCtx = {
            seasonLabel: Temporada,
            clientLabel: CustomerName,
            Temporada,
            CustomerName,
            CustomerId: ctxCustomerId,
          };
          const sketchBoards = moodboardsSorted.filter((m) =>
            isAiDesignBoard(m),
          );
          const moodOnly = moodboardsSorted.filter(
            (m) =>
              !isAiDesignBoard(m) &&
              !isResumenBoard(m) &&
              !isFichaTecnicaBoard(m) &&
              !isPropuestaBoard(m),
          );
          const disenosNode = {
            id: `${n2.id}::disenos`,
            label: folderDesigns,
            kind: "disenos",
            tasks,
            searches: [],
            moodboards: [],
            resumenes: [],
            count: tasks.length,
            ...branchCtx,
          };
          const sketchesNode =
            sketchBoards.length > 0
              ? {
                  id: `${n2.id}::sketches`,
                  label: folderSketches,
                  kind: "sketches",
                  tasks: [],
                  moodboards: sketchBoards,
                  aiDesigns: sketchBoards,
                  searches: [],
                  resumenes: [],
                  count: sketchBoards.length,
                  ...branchCtx,
                }
              : null;
          const searchsNode = {
            id: `${n2.id}::searchs`,
            label: folderSearchs,
            kind: "searchs",
            searches: searchesSorted,
            tasks: [],
            count: searchesSorted.length,
            ...branchCtx,
          };
          const moodboardsNode = {
            id: `${n2.id}::moodboards`,
            label: folderMoodboards,
            kind: "moodboards",
            moodboards: moodOnly,
            tasks: [],
            count: moodOnly.length,
            ...branchCtx,
          };
          const propuestasNode =
            propuestasSorted.length > 0
              ? {
                  id: `${n2.id}::propuestas`,
                  label: folderPropuestas,
                  kind: "propuestas",
                  propuestas: propuestasSorted,
                  moodboards: propuestasSorted,
                  tasks: [],
                  count: propuestasSorted.length,
                  ...branchCtx,
                }
              : null;
          const fichasNode =
            fichasSorted.length > 0
              ? {
                  id: `${n2.id}::fichas`,
                  label: folderFichas,
                  kind: "fichasTecnicas",
                  moodboards: fichasSorted,
                  fichasTecnicas: fichasSorted,
                  tasks: [],
                  count: fichasSorted.length,
                  ...branchCtx,
                }
              : null;
          const resumenesNode = {
            id: `${n2.id}::resumenes`,
            label: folderResumenes,
            kind: "resumenes",
            resumenes: resumenesSorted,
            moodboards: resumenesSorted,
            tasks: [],
            count: resumenesSorted.length,
            ...branchCtx,
          };
          const tileRows = rapportsSorted.filter(
            (row) => rapportToolKind(row) === "tartan",
          );
          const interpretRows = rapportsSorted.filter(
            (row) => rapportToolKind(row) === "seamless",
          );
          const rapportsNode = {
            id: `${n2.id}::rapports`,
            label: folderRapports,
            kind: "rapports",
            rapports: rapportsSorted,
            tasks: [],
            count: rapportsSorted.length,
            tilesNode: {
              id: `${n2.id}::rapports::tile`,
              label: folderRapportTiles,
              kind: "rapportTiles",
              rapports: tileRows,
              tasks: [],
              count: tileRows.length,
              ...branchCtx,
            },
            interpretNode: {
              id: `${n2.id}::rapports::interpret`,
              label: folderRapportInterpret,
              kind: "rapportInterpret",
              rapports: interpretRows,
              tasks: [],
              count: interpretRows.length,
              ...branchCtx,
            },
            ...branchCtx,
          };
          return {
            ...n2,
            tasks: [],
            searches: searchesSorted,
            moodboards: moodboardsSorted,
            propuestas: propuestasSorted,
            fichasTecnicas: fichasSorted,
            resumenes: resumenesSorted,
            rapports: rapportsSorted,
            disenosNode,
            sketchesNode,
            searchsNode,
            moodboardsNode,
            propuestasNode,
            fichasNode,
            resumenesNode,
            rapportsNode,
            count:
              tasks.length +
              sketchBoards.length +
              searchesSorted.length +
              moodOnly.length +
              propuestasSorted.length +
              fichasSorted.length +
              resumenesSorted.length +
              rapportsSorted.length,
          };
        })
        .sort((a, b) => sortLabel(a.label, b.label));
      const count = children.reduce((s, c) => s + c.count, 0);
      return { ...n1, children, count };
    })
    .sort((a, b) => {
      if (a.label === noSeason || a.label === noClient) return 1;
      if (b.label === noSeason || b.label === noClient) return -1;
      return sortLabel(a.label, b.label);
    });

  return roots;
}

/**
 * Camino de IDs (raíz → hoja) hasta `targetId` en el árbol Compás
 * (incluye hubs meetingsNode / disenosNode / etc.).
 */
export function findCompassTreePathIds(tree, targetId) {
  const want = String(targetId || "");
  if (!want) return [];
  const walk = (nodes, path) => {
    for (const n of nodes || []) {
      if (!n?.id) continue;
      const next = [...path, n.id];
      if (String(n.id) === want) return next;
      const hubs = [
        ...(Array.isArray(n.orgBranches) ? n.orgBranches : []),
        n.meetingsNode,
        n.disenosNode,
        n.sketchesNode,
        n.searchsNode,
        n.moodboardsNode,
        n.propuestasNode,
        n.fichasNode,
        n.resumenesNode,
        n.rapportsNode,
        n.rapportTiles,
        n.rapportInterpret,
        n.rapportsNode?.tilesNode,
        n.rapportsNode?.interpretNode,
        n.infoNode,
        n.produccionNode,
        n.presentacionesNode,
        n.statusNode,
      ].filter(Boolean);
      for (const h of hubs) {
        const found = walk([h], next);
        if (found) return found;
      }
      const found = walk(n.children, next);
      if (found) return found;
    }
    return null;
  };
  return walk(tree, []) || [];
}

/** Camino hasta el nodo cliente (por CustomerId) en Compás. */
export function findCompassClientPathIds(tree, customerId) {
  const cid = Number(customerId);
  if (!Number.isFinite(cid) || cid <= 0) return [];
  const walk = (nodes, path) => {
    for (const n of nodes || []) {
      if (!n?.id) continue;
      const next = [...path, n.id];
      if (n.kind === "client" && Number(n.CustomerId || n.customerId) === cid) {
        return next;
      }
      const kids = childrenForCompassColumn(n);
      const found = walk(kids, next);
      if (found) return found;
    }
    return null;
  };
  return walk(tree, []) || [];
}

/**
 * Camino de nodos a expandir en el árbol Compás para revelar una pieza
 * (task / search / moodboard / resumen / rapport) ya cargada en algún bundle.
 */
export function findCompassArtifactPathIds(
  tree,
  { taskId, searchId, moodboardId, resumenId, rapportId } = {},
) {
  const tk = taskId != null && String(taskId) !== "" ? Number(taskId) : null;
  const se =
    searchId != null && String(searchId) !== "" ? Number(searchId) : null;
  const mb =
    moodboardId != null && String(moodboardId) !== ""
      ? Number(moodboardId)
      : null;
  const rs =
    resumenId != null && String(resumenId) !== "" ? Number(resumenId) : null;
  const rp =
    rapportId != null && String(rapportId) !== "" ? Number(rapportId) : null;
  const hasAny = [tk, se, mb, rs, rp].some(
    (n) => n != null && Number.isFinite(n),
  );
  if (!hasAny) return [];

  const walkFolders = (folders, path) => {
    if (!folders) return null;
    const {
      disenosNode,
      sketchesNode,
      searchsNode,
      moodboardsNode,
      propuestasNode,
      fichasNode,
      resumenesNode,
      rapportsNode,
    } = folders;
    if (
      tk != null &&
      (disenosNode?.tasks || []).some((t) => Number(t.id) === tk)
    ) {
      return [...path, disenosNode.id].filter(Boolean);
    }
    if (
      mb != null &&
      (
        sketchesNode?.moodboards ||
        sketchesNode?.aiDesigns ||
        sketchesNode?.tasks ||
        []
      ).some((m) => Number(m.id) === mb)
    ) {
      return [...path, sketchesNode.id].filter(Boolean);
    }
    if (
      se != null &&
      (searchsNode?.searches || []).some((s) => Number(s.id) === se)
    ) {
      return [...path, searchsNode.id].filter(Boolean);
    }
    if (
      mb != null &&
      (moodboardsNode?.moodboards || []).some((m) => Number(m.id) === mb)
    ) {
      return [...path, moodboardsNode.id].filter(Boolean);
    }
    if (
      mb != null &&
      (propuestasNode?.propuestas || propuestasNode?.moodboards || []).some(
        (m) => Number(m.id) === mb,
      )
    ) {
      return [...path, propuestasNode.id].filter(Boolean);
    }
    if (
      mb != null &&
      (fichasNode?.fichasTecnicas || fichasNode?.moodboards || []).some(
        (m) => Number(m.id) === mb,
      )
    ) {
      return [...path, fichasNode.id].filter(Boolean);
    }
    if (
      (rs != null || mb != null) &&
      (resumenesNode?.resumenes || resumenesNode?.moodboards || []).some(
        (m) => Number(m.id) === (rs != null ? rs : mb),
      )
    ) {
      return [...path, resumenesNode.id].filter(Boolean);
    }
    if (rp != null && rapportsNode) {
      const all = rapportsNode.rapports || [];
      const row = all.find((r) => Number(r.id) === rp);
      if (row) {
        const sub =
          rapportToolKind(row) === "tartan"
            ? rapportsNode.tilesNode
            : rapportsNode.interpretNode;
        return [...path, rapportsNode.id, sub?.id].filter(Boolean);
      }
    }
    return null;
  };

  const walk = (nodes, path) => {
    for (const n of nodes || []) {
      if (!n?.id) continue;
      const next = [...path, n.id];
      if (n.kind === "project") {
        for (const branch of n.orgBranches || []) {
          if (!branch?.id) continue;
          const branchPath = [...next, branch.id];
          const hitFolders = walkFolders(branch, branchPath);
          if (hitFolders) return hitFolders;
          if (branch.meetingsNode) {
            const viaMeetings = walk([branch.meetingsNode], branchPath);
            if (viaMeetings) return viaMeetings;
          }
        }
      }
      if (n.kind === "orgBranch" || n.kind === "meeting") {
        const hit = walkFolders(n, next);
        if (hit) return hit;
        if (n.meetingsNode) {
          const viaMeetings = walk([n.meetingsNode], next);
          if (viaMeetings) return viaMeetings;
        }
      }
      if (n.kind === "meetings") {
        const hit = walk(n.children, next);
        if (hit) return hit;
      }
      const hit = walk(n.children, next);
      if (hit) return hit;
    }
    return null;
  };

  return walk(tree, []) || [];
}

/**
 * IDs de nodos a expandir para revelar un moodboard / resumen / search / task en el árbol.
 */
export function treeExpandIdsForSelection(
  tree,
  { moodboardId, resumenId, searchId, taskId, rapportId } = {},
) {
  const rp =
    rapportId != null && String(rapportId) !== "" ? Number(rapportId) : null;
  const rs =
    resumenId != null && String(resumenId) !== "" ? Number(resumenId) : null;
  const mb =
    moodboardId != null && String(moodboardId) !== ""
      ? Number(moodboardId)
      : null;
  const se =
    searchId != null && String(searchId) !== "" ? Number(searchId) : null;
  const tk = taskId != null && String(taskId) !== "" ? Number(taskId) : null;
  if (
    (mb == null || !Number.isFinite(mb)) &&
    (rs == null || !Number.isFinite(rs)) &&
    (se == null || !Number.isFinite(se)) &&
    (tk == null || !Number.isFinite(tk)) &&
    (rp == null || !Number.isFinite(rp))
  ) {
    return [];
  }
  for (const n1 of tree || []) {
    for (const n2 of n1.children || []) {
      if (rp != null && (n2.rapports || []).some((r) => Number(r.id) === rp)) {
        const row = (n2.rapports || []).find((r) => Number(r.id) === rp);
        const subId =
          rapportToolKind(row) === "tartan"
            ? n2.rapportsNode?.tilesNode?.id
            : n2.rapportsNode?.interpretNode?.id;
        return [n1.id, n2.id, n2.rapportsNode?.id, subId].filter(Boolean);
      }
      if (rs != null && (n2.resumenes || []).some((m) => Number(m.id) === rs)) {
        return [n1.id, n2.id, n2.resumenesNode?.id].filter(Boolean);
      }
      if (
        mb != null &&
        (n2.moodboards || []).some((m) => Number(m.id) === mb)
      ) {
        return [n1.id, n2.id, n2.moodboardsNode?.id].filter(Boolean);
      }
      if (
        mb != null &&
        (n2.propuestas || []).some((m) => Number(m.id) === mb)
      ) {
        return [n1.id, n2.id, n2.propuestasNode?.id].filter(Boolean);
      }
      if (
        mb != null &&
        (n2.fichasTecnicas || []).some((m) => Number(m.id) === mb)
      ) {
        return [n1.id, n2.id, n2.fichasNode?.id].filter(Boolean);
      }
      if (mb != null && (n2.resumenes || []).some((m) => Number(m.id) === mb)) {
        return [n1.id, n2.id, n2.resumenesNode?.id].filter(Boolean);
      }
      if (se != null && (n2.searches || []).some((s) => Number(s.id) === se)) {
        return [n1.id, n2.id, n2.searchsNode?.id].filter(Boolean);
      }
      if (
        tk != null &&
        (n2.disenosNode?.tasks || []).some((t) => Number(t.id) === tk)
      ) {
        return [n1.id, n2.id, n2.disenosNode?.id].filter(Boolean);
      }
      if (
        mb != null &&
        (n2.sketchesNode?.moodboards || n2.sketchesNode?.aiDesigns || []).some(
          (m) => Number(m.id) === mb,
        )
      ) {
        return [n1.id, n2.id, n2.sketchesNode?.id].filter(Boolean);
      }
      if (tk != null && (n2.tasks || []).some((t) => Number(t.id) === tk)) {
        return [n1.id, n2.id, n2.disenosNode?.id].filter(Boolean);
      }
    }
  }
  return [];
}

function artifactFolders(
  prefix,
  ctx,
  tasks,
  searches,
  moodboards,
  rapports,
  clientInfos = [],
) {
  const folderDesigns = tt("skillAgent.tree.folderDesigns", "SKILLS/AGENTES");
  const folderSketches = tt(
    "skillAgent.tree.folderSketches",
    "SKETCH / NEW DESIGNS",
  );
  const folderSearchs = tt("skillAgent.tree.folderSearchs", "SEARCHS");
  const folderMoodboards = tt("skillAgent.tree.folderMoodboards", "MOODBOARDS");
  const folderPropuestas = tt("skillAgent.tree.folderPropuestas", "PROPUESTAS");
  const folderFichas = tt(
    "skillAgent.tree.folderFichasTecnicas",
    "FICHAS TÉCNICAS",
  );
  const folderResumenes = tt("skillAgent.tree.folderResumenes", "RECAPS");
  const folderRapports = tt("skillAgent.tree.folderRapports", "PRINTS");
  const folderInfo = tt("skillAgent.tree.folderInfo", "CLIENT INFO");
  const folderProduccion = tt("skillAgent.tree.folderProduccion", "PRODUCCIÓN");
  const folderPresentaciones = tt(
    "skillAgent.tree.folderPresentaciones",
    "PRESENTACIONES PDF",
  );
  const folderStatus = tt("skillAgent.tree.folderStatus", "STATUS");
  const folderRapportTiles = tt("skillAgent.tree.folderRapportTiles", "TARTÁN");
  const folderRapportInterpret = tt(
    "skillAgent.tree.folderRapportInterpret",
    "SEAMLESS",
  );

  const tasksSorted = [...(tasks || [])].sort((a, b) =>
    String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
  );
  const searchesSorted = [...(searches || [])].sort((a, b) =>
    String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
  );
  const boards = Array.isArray(moodboards) ? moodboards : [];
  const moodboardsSorted = boards
    .filter(
      (m) =>
        !isResumenBoard(m) &&
        !isFichaTecnicaBoard(m) &&
        !isAiDesignBoard(m) &&
        !isPropuestaBoard(m),
    )
    .sort((a, b) =>
      String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
    );
  const propuestasSorted = boards
    .filter((m) => isPropuestaBoard(m))
    .sort((a, b) =>
      String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
    );
  const sketchesSorted = boards
    .filter((m) => isAiDesignBoard(m))
    .sort((a, b) =>
      String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
    );
  const fichasSorted = boards
    .filter((m) => isFichaTecnicaBoard(m))
    .sort((a, b) =>
      String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
    );
  const resumenesSorted = boards
    .filter((m) => isResumenBoard(m))
    .sort((a, b) =>
      String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
    );
  const rapportsSorted = [...(rapports || [])].sort((a, b) =>
    String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
  );
  const dossiersSorted = [...(clientInfos || [])].sort((a, b) =>
    String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
  );
  const clientInfosSorted = dossiersSorted.filter(isClientInfoRow);
  const produccionesSorted = dossiersSorted.filter(isProduccionRow);
  const presentacionesSorted = dossiersSorted.filter(isPresentacionesRow);
  const statusSorted = dossiersSorted.filter(isStatusRow);
  const tileRows = rapportsSorted.filter(
    (row) => rapportToolKind(row) === "tartan",
  );
  const interpretRows = rapportsSorted.filter(
    (row) => rapportToolKind(row) === "seamless",
  );

  /** Solo ramas con contenido (Compás: no pintar carpetas a 0). */
  const disenosNode =
    tasksSorted.length > 0
      ? {
          id: `${prefix}::disenos`,
          label: folderDesigns,
          kind: "disenos",
          tasks: tasksSorted,
          count: tasksSorted.length,
          ...ctx,
        }
      : null;
  const sketchesNode =
    sketchesSorted.length > 0
      ? {
          id: `${prefix}::sketches`,
          label: folderSketches,
          kind: "sketches",
          moodboards: sketchesSorted,
          aiDesigns: sketchesSorted,
          tasks: [],
          count: sketchesSorted.length,
          ...ctx,
        }
      : null;
  const searchsNode =
    searchesSorted.length > 0
      ? {
          id: `${prefix}::searchs`,
          label: folderSearchs,
          kind: "searchs",
          searches: searchesSorted,
          tasks: [],
          count: searchesSorted.length,
          ...ctx,
        }
      : null;
  const moodboardsNode =
    moodboardsSorted.length > 0
      ? {
          id: `${prefix}::moodboards`,
          label: folderMoodboards,
          kind: "moodboards",
          moodboards: moodboardsSorted,
          tasks: [],
          count: moodboardsSorted.length,
          ...ctx,
        }
      : null;
  const propuestasNode =
    propuestasSorted.length > 0
      ? {
          id: `${prefix}::propuestas`,
          label: folderPropuestas,
          kind: "propuestas",
          propuestas: propuestasSorted,
          moodboards: propuestasSorted,
          tasks: [],
          count: propuestasSorted.length,
          ...ctx,
        }
      : null;
  const fichasNode =
    fichasSorted.length > 0
      ? {
          id: `${prefix}::fichas`,
          label: folderFichas,
          kind: "fichasTecnicas",
          moodboards: fichasSorted,
          fichasTecnicas: fichasSorted,
          tasks: [],
          count: fichasSorted.length,
          ...ctx,
        }
      : null;
  const resumenesNode =
    resumenesSorted.length > 0
      ? {
          id: `${prefix}::resumenes`,
          label: folderResumenes,
          kind: "resumenes",
          resumenes: resumenesSorted,
          moodboards: resumenesSorted,
          tasks: [],
          count: resumenesSorted.length,
          ...ctx,
        }
      : null;
  const rapportsNode =
    rapportsSorted.length > 0
      ? {
          id: `${prefix}::rapports`,
          label: folderRapports,
          kind: "rapports",
          rapports: rapportsSorted,
          tasks: [],
          count: rapportsSorted.length,
          tilesNode:
            tileRows.length > 0
              ? {
                  id: `${prefix}::rapports::tile`,
                  label: folderRapportTiles,
                  kind: "rapportTiles",
                  rapports: tileRows,
                  tasks: [],
                  count: tileRows.length,
                  ...ctx,
                }
              : null,
          interpretNode:
            interpretRows.length > 0
              ? {
                  id: `${prefix}::rapports::interpret`,
                  label: folderRapportInterpret,
                  kind: "rapportInterpret",
                  rapports: interpretRows,
                  tasks: [],
                  count: interpretRows.length,
                  ...ctx,
                }
              : null,
          ...ctx,
        }
      : null;

  // Solo si hay contenido. Crear desde + del proyecto / hub.
  const infoNode =
    clientInfosSorted.length > 0
      ? {
          id: `${prefix}::info`,
          label: folderInfo,
          kind: "clientInfos",
          clientInfos: clientInfosSorted,
          tasks: [],
          count: clientInfosSorted.length,
          ...ctx,
        }
      : null;
  const produccionNode =
    produccionesSorted.length > 0
      ? {
          id: `${prefix}::produccion`,
          label: folderProduccion,
          kind: "producciones",
          clientInfos: produccionesSorted,
          producciones: produccionesSorted,
          tasks: [],
          count: produccionesSorted.length,
          ...ctx,
        }
      : null;
  const presentacionesNode =
    presentacionesSorted.length > 0
      ? {
          id: `${prefix}::presentaciones`,
          label: folderPresentaciones,
          kind: "presentaciones",
          clientInfos: presentacionesSorted,
          presentaciones: presentacionesSorted,
          tasks: [],
          count: presentacionesSorted.length,
          ...ctx,
        }
      : null;
  const statusNode =
    statusSorted.length > 0
      ? {
          id: `${prefix}::status`,
          label: folderStatus,
          kind: "status",
          clientInfos: statusSorted,
          statuses: statusSorted,
          tasks: [],
          count: statusSorted.length,
          ...ctx,
        }
      : null;

  return {
    disenosNode,
    sketchesNode,
    searchsNode,
    moodboardsNode,
    propuestasNode,
    fichasNode,
    resumenesNode,
    rapportsNode,
    infoNode,
    produccionNode,
    presentacionesNode,
    statusNode,
    count:
      tasksSorted.length +
      sketchesSorted.length +
      searchesSorted.length +
      moodboardsSorted.length +
      propuestasSorted.length +
      fichasSorted.length +
      resumenesSorted.length +
      rapportsSorted.length +
      clientInfosSorted.length +
      produccionesSorted.length +
      presentacionesSorted.length +
      statusSorted.length,
  };
}

function splitByMeeting(rows = []) {
  const atProject = [];
  const byMeeting = new Map();
  for (const row of rows || []) {
    const mid = Number(row?.MeetingSessionId);
    if (Number.isFinite(mid) && mid > 0) {
      if (!byMeeting.has(mid)) byMeeting.set(mid, []);
      byMeeting.get(mid).push(row);
    } else {
      atProject.push(row);
    }
  }
  return { atProject, byMeeting };
}

/**
 * Si una pieza apunta a un MeetingSessionId que no está en el bundle del
 * proyecto (reunión movida / sync), no se pierde: vuelve al nivel proyecto.
 */
export function reclaimOrphanMeetingArtifacts(split, meetings = []) {
  const known = new Set(
    (meetings || [])
      .map((m) => Number(m.id))
      .filter((id) => Number.isFinite(id) && id > 0),
  );
  const atProject = [...(split?.atProject || [])];
  const byMeeting = new Map();
  for (const [mid, rows] of split?.byMeeting || []) {
    const id = Number(mid);
    if (known.has(id)) {
      byMeeting.set(id, rows);
    } else {
      atProject.push(...(rows || []));
    }
  }
  return { atProject, byMeeting };
}

function compassProjectSearchBlob(group, project, bundle) {
  const parts = [
    project?.name,
    project?.temporada,
    project?.customerName,
    group?.temporada,
    group?.customerName,
  ];
  if (bundle) {
    for (const m of bundle.meetings || [])
      parts.push(m?.title, m?.customerName);
    for (const r of bundle.tasks || [])
      parts.push(r?.Titulo, r?.Brief, r?.Temporada, r?.CustomerName);
    for (const r of bundle.searches || [])
      parts.push(r?.Titulo, r?.Temporada, r?.CustomerName, r?.Tipologia);
    for (const r of bundle.moodboards || [])
      parts.push(r?.Titulo, r?.CategoryLabel, r?.Temporada, r?.CustomerName);
    for (const r of bundle.rapports || [])
      parts.push(r?.Titulo, r?.Temporada, r?.CustomerName);
    for (const r of bundle.clientInfos || [])
      parts.push(r?.Titulo, r?.Temporada, r?.CustomerName);
  }
  return parts.filter(Boolean).join(" ").toLowerCase();
}

/**
 * Árbol Compás.
 * groupBy "season" → Temporada → Cliente → Proyecto
 * groupBy "client" → Cliente → Temporada → Proyecto
 * `bundlesByProjectId` = { [projectId]: { meetings, tasks, searches, moodboards, rapports } }
 * Sin bundle, el proyecto muestra solo el conteo (carga lazy al expandir).
 * `filterQuery` filtra proyectos por nombre / cliente / temporada / piezas cargadas.
 */
export function buildCompassProjectsTree(
  rootGroups = [],
  bundlesByProjectId = {},
  groupBy = "season",
  filterQuery = "",
) {
  const folderMeetings = tt("skillAgent.tree.folderMeetings", "MEETINGS");
  const sortLabel = (a, b) =>
    String(a).localeCompare(String(b), i18n.language || "es", {
      numeric: true,
      sensitivity: "base",
    });
  const noSeason = tt("skillAgent.tree.noSeason", "Sin temporada");
  const noClient = tt("skillAgent.tree.noClient", "Sin cliente");
  const byClient = groupBy === "client";
  const qq = String(filterQuery || "")
    .trim()
    .toLowerCase();

  const l1Map = new Map();

  for (const group of Array.isArray(rootGroups) ? rootGroups : []) {
    const season = String(group.temporada || "").trim() || noSeason;
    const client = String(group.customerName || "").trim() || noClient;
    const L1 = byClient ? client : season;
    const L2 = byClient ? season : client;
    const l1Kind = byClient ? "client" : "season";
    const l2Kind = byClient ? "season" : "client";
    const l1Id = byClient
      ? `l1:${group.customerId || 0}::${client}`
      : `l1:${season}`;
    const l2Key = byClient ? season : `${group.customerId || 0}::${client}`;
    const l2Id = `l2:${l1Id}::${l2Key}`;

    if (!l1Map.has(l1Id)) {
      l1Map.set(l1Id, {
        id: l1Id,
        label: L1,
        kind: l1Kind,
        ...(byClient
          ? {
              CustomerId: group.customerId || null,
              CustomerName: client,
            }
          : {}),
        children: new Map(),
        count: 0,
      });
    }
    const l1Node = l1Map.get(l1Id);
    if (!l1Node.children.has(l2Key)) {
      l1Node.children.set(l2Key, {
        id: l2Id,
        label: L2,
        kind: l2Kind,
        CustomerId: group.customerId || null,
        CustomerName: client,
        Temporada: season === noSeason ? "" : season,
        children: [],
        count: 0,
      });
    }
    const l2Node = l1Node.children.get(l2Key);

    for (const p of group.projects || []) {
      const projectId = Number(p.id);
      const bundle = bundlesByProjectId?.[projectId] || null;
      if (qq && !compassProjectSearchBlob(group, p, bundle).includes(qq)) {
        continue;
      }
      const ctx = {
        ProjectId: projectId,
        projectId,
        Temporada: p.temporada || (season === noSeason ? "" : season),
        CustomerId: p.customerId || group.customerId || null,
        CustomerName: p.customerName || client,
        seasonLabel: p.temporada || season,
        clientLabel: p.customerName || client,
      };

      let projectNode;
      const meetingCount = Number(p.meetingCount) || 0;
      const artifactCount = Number(p.artifactCount) || 0;
      if (!bundle) {
        projectNode = {
          id: `project:${projectId}`,
          label: p.name || `Proyecto #${projectId}`,
          kind: "project",
          loaded: false,
          meetingCount,
          artifactCount,
          count: Number(p.count) || meetingCount + artifactCount,
          children: [],
          ...ctx,
        };
      } else {
        const meetings = Array.isArray(bundle.meetings) ? bundle.meetings : [];
        const taskSplit = reclaimOrphanMeetingArtifacts(
          splitByMeeting(bundle.tasks),
          meetings,
        );
        const searchSplit = reclaimOrphanMeetingArtifacts(
          splitByMeeting(bundle.searches),
          meetings,
        );
        const moodSplit = reclaimOrphanMeetingArtifacts(
          splitByMeeting(bundle.moodboards),
          meetings,
        );
        const rapportSplit = reclaimOrphanMeetingArtifacts(
          splitByMeeting(bundle.rapports),
          meetings,
        );
        const clientInfoSplit = reclaimOrphanMeetingArtifacts(
          splitByMeeting(bundle.clientInfos),
          meetings,
        );

        const meetingsByBrand = partitionRowsByOrgBrand(meetings);
        const atProjectTasksByBrand = partitionRowsByOrgBrand(
          taskSplit.atProject,
        );
        const atProjectSearchesByBrand = partitionRowsByOrgBrand(
          searchSplit.atProject,
        );
        const atProjectMoodsByBrand = partitionRowsByOrgBrand(
          moodSplit.atProject,
        );
        const atProjectRapportsByBrand = partitionRowsByOrgBrand(
          rapportSplit.atProject,
        );
        const atProjectInfosByBrand = partitionRowsByOrgBrand(
          clientInfoSplit.atProject,
        );

        const folderMeetingsLabel = folderMeetings;
        const orgBranches = [];
        for (const brand of ORG_BRANDS) {
          const brandMeetings = meetingsByBrand[brand] || [];
          const meetingChildren = brandMeetings.map((m) => {
            const mid = Number(m.id);
            const mCtx = {
              ...ctx,
              OrgBrand: brand,
              orgBrand: brand,
              MeetingSessionId: mid,
              meetingSessionId: mid,
              meetingTitle: m.title || `Reunión #${mid}`,
            };
            const folders = artifactFolders(
              `meeting:${mid}`,
              mCtx,
              taskSplit.byMeeting.get(mid) || [],
              searchSplit.byMeeting.get(mid) || [],
              moodSplit.byMeeting.get(mid) || [],
              rapportSplit.byMeeting.get(mid) || [],
              clientInfoSplit.byMeeting.get(mid) || [],
            );
            return {
              id: `meeting:${mid}`,
              label: m.title || `Reunión #${mid}`,
              kind: "meeting",
              meeting: m,
              count: folders.count,
              ...folders,
              ...mCtx,
            };
          });

          const brandCtx = {
            ...ctx,
            OrgBrand: brand,
            orgBrand: brand,
            MeetingSessionId: null,
          };
          const projectFolders = artifactFolders(
            `project:${projectId}::org:${brand}`,
            brandCtx,
            atProjectTasksByBrand[brand] || [],
            atProjectSearchesByBrand[brand] || [],
            atProjectMoodsByBrand[brand] || [],
            atProjectRapportsByBrand[brand] || [],
            atProjectInfosByBrand[brand] || [],
          );

          const meetingsNode =
            meetingChildren.length > 0
              ? {
                  id: `project:${projectId}::org:${brand}::meetings`,
                  label: folderMeetingsLabel,
                  kind: "meetings",
                  children: meetingChildren,
                  count: meetingChildren.length,
                  ...brandCtx,
                }
              : null;

          const meetingCount = meetingsNode ? meetingsNode.count : 0;
          const artifactCount = Number(projectFolders.count) || 0;
          const branchCount = meetingCount + artifactCount;
          // Sin contenido → no pintar la rama; NML/STAGE se elige en el dialog al crear.
          if (branchCount <= 0) continue;

          orgBranches.push({
            id: `project:${projectId}::org:${brand}`,
            label: brand,
            kind: "orgBranch",
            OrgBrand: brand,
            orgBrand: brand,
            meetingsNode,
            ...projectFolders,
            meetingCount,
            artifactCount,
            count: branchCount,
            children: [],
            ...brandCtx,
          });
        }

        const loadedMeetingCount = orgBranches.reduce(
          (n, b) => n + (Number(b.meetingCount) || 0),
          0,
        );
        const loadedArtifactCount = orgBranches.reduce(
          (n, b) => n + (Number(b.artifactCount) || 0),
          0,
        );
        projectNode = {
          id: `project:${projectId}`,
          label: p.name || `Proyecto #${projectId}`,
          kind: "project",
          loaded: true,
          orgBranches,
          meetingCount: loadedMeetingCount,
          artifactCount: loadedArtifactCount,
          count: loadedMeetingCount + loadedArtifactCount,
          children: [],
          ...ctx,
        };
      }

      l2Node.children.push(projectNode);
      l2Node.count += projectNode.count || 0;
    }
  }

  const trailer = byClient ? noClient : noSeason;

  return [...l1Map.values()]
    .map((l1Node) => {
      const children = [...l1Node.children.values()]
        .filter((c) => (c.children || []).length > 0)
        .map((c) => ({
          ...c,
          children: [...(c.children || [])].sort((a, b) =>
            sortLabel(a.label, b.label),
          ),
        }))
        .sort((a, b) => {
          if (a.label === (byClient ? noSeason : noClient)) return 1;
          if (b.label === (byClient ? noSeason : noClient)) return -1;
          return sortLabel(a.label, b.label);
        });
      const count = children.reduce((s, c) => s + (c.count || 0), 0);
      return { ...l1Node, children, count };
    })
    .filter((n) => (n.children || []).length > 0)
    .sort((a, b) => {
      if (a.label === trailer) return 1;
      if (b.label === trailer) return -1;
      return sortLabel(a.label, b.label);
    });
}

/** Filtro exclusivo de tipo de pieza en el árbol Compás. */
export const TREE_TYPE_FILTERS = Object.freeze([
  "all",
  "meetings",
  "moodboards",
  "propuestas",
  "prints",
  "clientInfo",
  "produccion",
  "presentaciones",
  "status",
  "searchs",
  "disenos",
  "fichas",
  "resumenes",
]);

const HUB_KEYS_BY_FILTER = {
  meetings: ["meetingsNode"],
  moodboards: ["moodboardsNode"],
  propuestas: ["propuestasNode"],
  prints: ["rapportsNode"],
  clientInfo: ["infoNode"],
  produccion: ["produccionNode"],
  presentaciones: ["presentacionesNode"],
  status: ["statusNode"],
  searchs: ["searchsNode"],
  disenos: ["disenosNode", "sketchesNode"],
  fichas: ["fichasNode"],
  resumenes: ["resumenesNode"],
};

const ALL_HUB_KEYS = [
  "meetingsNode",
  "disenosNode",
  "sketchesNode",
  "searchsNode",
  "moodboardsNode",
  "propuestasNode",
  "fichasNode",
  "resumenesNode",
  "rapportsNode",
  "infoNode",
  "produccionNode",
  "presentacionesNode",
  "statusNode",
];

function recountHubs(node) {
  let count = 0;
  for (const key of ALL_HUB_KEYS) {
    const hub = node[key];
    if (!hub) continue;
    if (key === "meetingsNode") {
      count += Number(hub.count) || (hub.children || []).length || 0;
    } else {
      count += Number(hub.count) || 0;
    }
  }
  return count;
}

function filterMeetingNode(meeting, typeFilter) {
  if (typeFilter === "meetings") {
    return {
      ...meeting,
      disenosNode: null,
      sketchesNode: null,
      searchsNode: null,
      moodboardsNode: null,
      propuestasNode: null,
      fichasNode: null,
      resumenesNode: null,
      rapportsNode: null,
      infoNode: null,
      produccionNode: null,
      presentacionesNode: null,
      statusNode: null,
      count: 0,
    };
  }
  const keepKeys = HUB_KEYS_BY_FILTER[typeFilter] || [];
  const next = { ...meeting };
  for (const key of ALL_HUB_KEYS) {
    if (key === "meetingsNode") continue;
    next[key] = keepKeys.includes(key) ? meeting[key] || null : null;
  }
  next.count = recountHubs(next);
  if (next.count <= 0) return null;
  return next;
}

function filterOrgOrContainer(node, typeFilter) {
  const keepKeys = HUB_KEYS_BY_FILTER[typeFilter] || [];
  const next = { ...node };

  if (typeFilter === "meetings") {
    const meetings = node.meetingsNode;
    if (!meetings) return null;
    const children = (meetings.children || [])
      .map((m) => filterMeetingNode(m, typeFilter))
      .filter(Boolean);
    if (!children.length) return null;
    next.meetingsNode = {
      ...meetings,
      children,
      count: children.length,
    };
    for (const key of ALL_HUB_KEYS) {
      if (key !== "meetingsNode") next[key] = null;
    }
    next.count = children.length;
    return next;
  }

  for (const key of ALL_HUB_KEYS) {
    if (key === "meetingsNode") {
      const meetings = node.meetingsNode;
      if (!meetings) {
        next.meetingsNode = null;
        continue;
      }
      const children = (meetings.children || [])
        .map((m) => filterMeetingNode(m, typeFilter))
        .filter(Boolean);
      next.meetingsNode = children.length
        ? { ...meetings, children, count: children.length }
        : null;
      continue;
    }
    next[key] = keepKeys.includes(key) ? node[key] || null : null;
  }

  next.count = recountHubs(next);
  if (next.count <= 0 && !(next.orgBranches || []).length) return null;
  return next;
}

function filterProjectNode(project, typeFilter) {
  if (!typeFilter || typeFilter === "all") return project;
  const branches = (project.orgBranches || [])
    .map((b) => filterOrgOrContainer(b, typeFilter))
    .filter(Boolean);
  if (!branches.length) return null;
  const count = branches.reduce((s, b) => s + (Number(b.count) || 0), 0);
  return { ...project, orgBranches: branches, count };
}

/**
 * Filtra el árbol Compás por tipo de pieza. Si un proyecto/org queda vacío, se omite.
 */
export function filterCompassTreeByType(tree, typeFilter) {
  const filter = String(typeFilter || "all").trim();
  if (!filter || filter === "all") return tree || [];

  const walkL2 = (l2) => {
    const children = (l2.children || [])
      .map((p) => filterProjectNode(p, filter))
      .filter(Boolean);
    if (!children.length) return null;
    const count = children.reduce((s, c) => s + (Number(c.count) || 0), 0);
    return { ...l2, children, count };
  };

  return (tree || [])
    .map((l1) => {
      const children = (l1.children || []).map(walkL2).filter(Boolean);
      if (!children.length) return null;
      const count = children.reduce((s, c) => s + (Number(c.count) || 0), 0);
      return { ...l1, children, count };
    })
    .filter(Boolean);
}

/** Ids de proyecto en tree-roots (para precargar bundles al filtrar). */
export function projectIdsFromTreeRoots(roots = []) {
  const ids = [];
  for (const g of Array.isArray(roots) ? roots : []) {
    for (const p of g?.projects || []) {
      const id = Number(p?.id ?? p?.ProjectId);
      if (Number.isFinite(id) && id > 0) ids.push(id);
    }
  }
  return [...new Set(ids)];
}

/**
 * Ids de nodos a expandir para revelar todo el contenido filtrado
 * (temporada → cliente → proyecto → org → carpetas).
 */
export function collectCompassExpandIds(tree = []) {
  const ids = [];
  const walk = (nodes) => {
    for (const n of nodes || []) {
      if (!n?.id) continue;
      ids.push(n.id);
      if (n.orgBranches?.length) walk(n.orgBranches);
      for (const key of ALL_HUB_KEYS) {
        const hub = n[key];
        if (!hub) continue;
        ids.push(hub.id);
        if (hub.tilesNode?.id) ids.push(hub.tilesNode.id);
        if (hub.interpretNode?.id) ids.push(hub.interpretNode.id);
        if (hub.children?.length) walk(hub.children);
      }
      if (n.children?.length) walk(n.children);
    }
  };
  walk(tree);
  return ids;
}

/**
 * Hijos navegables (siguiente columna) para un nodo Compás.
 * `null` = raíz → usar el array `tree` como primera columna.
 */
export function childrenForCompassColumn(node) {
  if (!node) return [];
  const kind = node.kind;
  if (kind === "project") {
    return Array.isArray(node.orgBranches) ? [...node.orgBranches] : [];
  }
  if (kind === "orgBranch" || kind === "meeting") {
    return [
      node.meetingsNode,
      node.disenosNode,
      node.sketchesNode,
      node.searchsNode,
      node.moodboardsNode,
      node.propuestasNode,
      node.fichasNode,
      node.resumenesNode,
      node.rapportsNode,
      node.infoNode,
      node.produccionNode,
      node.presentacionesNode,
      node.statusNode,
    ].filter(Boolean);
  }
  if (kind === "rapports") {
    return [node.tilesNode, node.interpretNode].filter(Boolean);
  }
  return Array.isArray(node.children) ? [...node.children] : [];
}

/** Hojas (artefactos) que se listan en la columna de un hub. */
export function leafItemsForCompassColumn(node) {
  if (!node) return [];
  const kind = node.kind;
  if (kind === "searchs") {
    return (node.searches || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "search",
      id: `search:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (kind === "moodboards") {
    return (node.moodboards || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "moodboard",
      id: `moodboard:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (kind === "propuestas") {
    return (node.propuestas || node.moodboards || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "propuesta",
      id: `propuesta:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (kind === "fichasTecnicas") {
    return (node.fichasTecnicas || node.moodboards || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "moodboard",
      id: `moodboard:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (kind === "resumenes") {
    return (node.resumenes || node.moodboards || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "resumen",
      id: `resumen:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (kind === "rapportTiles" || kind === "rapportInterpret") {
    return (node.rapports || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "rapport",
      id: `rapport:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (
    kind === "clientInfos" ||
    kind === "producciones" ||
    kind === "presentaciones" ||
    kind === "status"
  ) {
    return (node.clientInfos || []).map((item) => ({
      entryKind: "leaf",
      leafKind:
        kind === "producciones"
          ? "produccion"
          : kind === "presentaciones"
            ? "presentacion"
            : kind === "status"
              ? "status"
              : "clientInfo",
      id: `clientInfo:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (kind === "disenos") {
    return (node.tasks || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "task",
      id: `task:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  if (kind === "sketches") {
    return (node.moodboards || node.aiDesigns || []).map((item) => ({
      entryKind: "leaf",
      leafKind: "aiDesign",
      id: `aiDesign:${item.id}`,
      label: item.Titulo || `#${item.id}`,
      item,
    }));
  }
  return [];
}

/** Comparación alfabética (números naturales, sin distinguir mayúsculas). */
export function compareCompassLabel(a, b) {
  return String(a || "").localeCompare(String(b || ""), i18n.language || "es", {
    numeric: true,
    sensitivity: "base",
  });
}

/** Entradas unificadas de una columna: nodos hijos + hojas, A→Z. */
export function entriesForCompassColumn(node, roots = []) {
  const childNodes = node ? childrenForCompassColumn(node) : roots || [];
  const folders = childNodes.map((n) => ({
    entryKind: "node",
    id: n.id,
    label: n.label,
    kind: n.kind,
    count: n.count,
    node: n,
  }));
  const leaves = node ? leafItemsForCompassColumn(node) : [];
  return [...folders, ...leaves].sort((a, b) =>
    compareCompassLabel(a.label, b.label),
  );
}

/** Resuelve nodos de un path de ids (trunca si algún id ya no existe). */
export function resolveCompassColumnPath(tree, pathIds = []) {
  const nodes = [];
  let level = tree || [];
  for (const rawId of pathIds || []) {
    const id = String(rawId || "");
    if (!id) break;
    const found = (level || []).find((n) => String(n?.id) === id);
    if (!found) break;
    nodes.push(found);
    level = childrenForCompassColumn(found);
  }
  return nodes;
}

/**
 * Etiqueta de cabecera de columna Compás según lo que lista (no el padre).
 * @returns {"client"|"season"|"projects"|"empresa"|"conceptos"|null}
 */
export function compassColumnCategory(parent, entries = []) {
  const firstNode = (entries || []).find((e) => e?.entryKind === "node");
  const childKind = firstNode?.kind || null;
  if (childKind === "client") return "client";
  if (childKind === "season") return "season";
  if (childKind === "project") return "projects";
  if (childKind === "orgBranch") return "empresa";
  if (
    childKind === "meetings" ||
    childKind === "disenos" ||
    childKind === "sketches" ||
    childKind === "searchs" ||
    childKind === "moodboards" ||
    childKind === "propuestas" ||
    childKind === "fichasTecnicas" ||
    childKind === "resumenes" ||
    childKind === "rapports" ||
    childKind === "clientInfos" ||
    childKind === "producciones" ||
    childKind === "presentaciones" ||
    childKind === "status"
  ) {
    return "conceptos";
  }
  // Fallback por padre cuando la columna aún no tiene hijos (proyecto sin bundle).
  if (parent?.kind === "season") return "client";
  if (parent?.kind === "client") {
    const kids = childrenForCompassColumn(parent);
    if (kids.some((k) => k?.kind === "season")) return "season";
    return "projects";
  }
  if (parent?.kind === "project") return "empresa";
  if (parent?.kind === "orgBranch" || parent?.kind === "meeting") {
    return "conceptos";
  }
  return null;
}

const COMPASS_LIBRARY_HUB_KINDS = new Set([
  "meetings",
  "disenos",
  "sketches",
  "searchs",
  "moodboards",
  "propuestas",
  "fichasTecnicas",
  "resumenes",
  "rapports",
  "rapportTiles",
  "rapportInterpret",
  "clientInfos",
  "producciones",
  "presentaciones",
  "status",
]);

/** Hijo para auto-expand: prioriza el que tenga contenido; en proyecto, STAGE. */
export function pickCompassAutoChild(children, { preferStage = false } = {}) {
  const list = (children || []).filter(Boolean);
  if (!list.length) return null;
  const withCount = (n) => (Number(n?.count) || 0) > 0;
  if (preferStage) {
    const stageWith = list.find(
      (b) =>
        normalizeOrgBrand(b?.orgBrand || b?.OrgBrand) === "STAGE" &&
        withCount(b),
    );
    if (stageWith) return stageWith;
    // Si STAGE está vacío pero otra empresa tiene algo → esa.
    const anyWith = list.find(withCount);
    if (anyWith) return anyWith;
    const stage = list.find(
      (b) => normalizeOrgBrand(b?.orgBrand || b?.OrgBrand) === "STAGE",
    );
    if (stage) return stage;
  }
  return list.find(withCount) || list[0];
}

/** Hubs que listan hojas (SEARCHS, PRINTS…) — ahí paramos. MEETINGS sigue a la reunión. */
function isCompassLeafLibraryHub(kind) {
  return COMPASS_LIBRARY_HUB_KINDS.has(kind) && kind !== "meetings";
}

/**
 * Extiende el path hasta el contenido más profundo disponible:
 * … → Empresa → MEETINGS (si hay) → reunión → primer concepto con piezas.
 * Si no hay meetings, entra en el primer hub del proyecto (SEARCHS, etc.).
 */
export function extendCompassPathToOrgBranch(tree, pathIds = []) {
  const ids = (pathIds || []).map(String).filter(Boolean);
  for (let guard = 0; guard < 12; guard += 1) {
    const nodes = resolveCompassColumnPath(tree, ids);
    if (ids.length && nodes.length < ids.length) {
      return nodes.map((n) => String(n.id));
    }
    const last = nodes.length ? nodes[nodes.length - 1] : null;
    // Ya en un hub de piezas (SEARCHS, MOODBOARDS…) → mostrar lista, no seguir.
    if (last && isCompassLeafLibraryHub(last.kind)) {
      return nodes.map((n) => String(n.id));
    }
    const children = last
      ? childrenForCompassColumn(last)
      : Array.isArray(tree)
        ? tree
        : [];
    if (!children.length) {
      return nodes.map((n) => String(n.id));
    }
    const next = pickCompassAutoChild(children, {
      preferStage: last?.kind === "project",
    });
    if (!next?.id) return nodes.map((n) => String(n.id));
    ids.push(String(next.id));
    // Entrar en hub de piezas y parar (la siguiente columna son las hojas).
    if (isCompassLeafLibraryHub(next.kind)) {
      return resolveCompassColumnPath(tree, ids).map((n) => String(n.id));
    }
    // meetings / meeting / orgBranch / project… → seguir si hay más.
  }
  return resolveCompassColumnPath(tree, ids).map((n) => String(n.id));
}

/** Primer projectId del path (si hay) para disparar carga de bundle. */
export function projectIdFromCompassPath(tree, pathIds = []) {
  const nodes = resolveCompassColumnPath(tree, pathIds);
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const n = nodes[i];
    if (n?.kind === "project") {
      const pid = Number(n.ProjectId || n.projectId);
      return Number.isFinite(pid) && pid > 0 ? pid : null;
    }
  }
  return null;
}
